import { initShareButtons } from './share';
import { contentStoreInstance } from './store/content';
import { initShaderChunk } from './view/three/shaders/ShaderChunk';
import { FontsStorage } from './view/three/factories/FontsStorage';
import SpaceView, { ViewEvent } from './view/SpaceView';
import { TexturesStorage } from './view/three/factories/TexturesStorage';
import NavScrollTriggerController from './controller/NavScrollTriggerController';
import NavigationMediator from './controller/NavigationMediator';
import HeaderUnitsHoverMediator from './controller/HeaderUnitsHoverMediator';
import PopupConstructor, { PopupConstructorEvent } from './view/dom/PopupConstructor';
import SectionView from './view/three/section/SectionView';
import { UNIT } from './constants/size';
import { ActionTypes } from './constants/ActionTypes';
import { analyticsEvent } from './analytics';
import DOMActionButtonMediator, { DOMActionButtonEvent } from './controller/DOMActionButtonMediator';

const capitalize = (s) => `${s.substr(0, 1).toUpperCase()}${s.substr(1)}`;

const addSpaceView = (scrollWeight) => {
    const view = new SpaceView();

    // Pass the contentStore ref to the Shell or SpaceView in order to sync scrollWeight value in the dynamic case
    view.threeViewShell.scrollWeight = scrollWeight;

    view.appendCanvasToElement('.canvas-container');

    return view;
};

const addContent = (contentStore, view) => {
    const shell = view.threeViewShell;
    const webgl = shell.infrastructure;

    const articleHeaderView = new SectionView(contentStore.articleHeader, webgl);
    const sectionsView = contentStore.sections.map((sectionData) => new SectionView(sectionData, webgl));
    const footerView = new SectionView(contentStore.footer, webgl, -0.3);
    let units = [[], ...sectionsView].reduce((accumulate, section) => accumulate.concat(section.units));

    units = [...articleHeaderView.units, ...units, ...footerView.units];

    // - add sections in an queue

    // Start at the middle of the first vh:
    let zIterate = -0.5 * UNIT.zSize;

    articleHeaderView.position.z = -zIterate;
    shell.add(articleHeaderView);
    zIterate += articleHeaderView.zSize;

    sectionsView.forEach((section) => {
        section.position.z = -zIterate;
        shell.add(section);

        zIterate += section.zSize;
    });

    footerView.position.z = -zIterate;
    shell.add(footerView);
    zIterate += footerView.zSize;

    return {
        articleHeaderView,
        sectionsView,
        footerView,
        units
    };
};

// Utils:
const getNameMap = (list) => new Map(list.map((item) => [item.name, item]));

const disableLink = (link) => {
    link.setAttribute('aria-disabled', true);
    link.removeAttribute('href');
};

const enableLink = (link) => {
    link.removeAttribute('aria-disabled');
    link.setAttribute('href', link.getAttribute('data-href'));
};

const disableButton = (link) => {
    link.setAttribute('aria-disabled', true);
    link.setAttribute('disabled', true);
    link.classList.add('disabled');
};

const enableButton = (link) => {
    link.removeAttribute('aria-disabled');
    link.removeAttribute('disabled');
    link.classList.remove('disabled');
};

const _checkHeaderLinksVisibility = (navRoot, checkButtons = false) => {
    if (!navRoot) return;

    const rootRect = navRoot.getBoundingClientRect();
    const navLinks = checkButtons ? [...navRoot.querySelectorAll('button')] : [...navRoot.querySelectorAll('a')];

    navLinks.forEach((link) => {
        const rect = link.getBoundingClientRect();

        const isInsideRoot = rect.left >= rootRect.left
            && (rect.left + rect.width <= rootRect.left + rootRect.width)
            && rect.top >= rootRect.top
            && (rect.top + rect.height <= rootRect.top + rootRect.height);

        if (checkButtons) {
            if (isInsideRoot) enableButton(link);
            else disableButton(link);
        } else if (isInsideRoot) enableLink(link);
        else disableLink(link);
    });
};

const _initHeaderNavHelper = () => {
    const navRoot = document.querySelector('.header-navigation');
    const shareRoot = document.querySelector('.share');

    _checkHeaderLinksVisibility(navRoot);
    _checkHeaderLinksVisibility(shareRoot, true);

    window.addEventListener('resize', () => {
        _checkHeaderLinksVisibility(navRoot);
        _checkHeaderLinksVisibility(shareRoot, true);
    });
};
// / Utils

class MainApp {
    constructor(lang) {
        initShaderChunk();

        this._construct(lang);
    }

    _construct(lang) {
        //
        // Initialize data - lazy
        // (Could be made dynamic later if it's necessary)
        const contentStore = contentStoreInstance(lang);

        //
        // 1. Space: infrastructure, scene and background
        const view = addSpaceView(contentStore.scrollWeight);

        //
        // 2. Scroll navigation
        // - to the camera
        const navController = new NavScrollTriggerController(view);

        // - menu mediator
        const navMediator = new NavigationMediator('.page__header');

        // - links and buttons mouseover mediator
        const linksButtonsMediator = new HeaderUnitsHoverMediator(['data-href', 'data-social'], '.page__header');

        // - action buttons mouseover mediator
        const actionButtonsMediator = new DOMActionButtonMediator('.main');

        //
        // 3. Click actions
        // - review popups
        const popupConstructor = new PopupConstructor();

        //
        // Store references:
        //
        this.spaceView = view;
        this.navController = navController;
        this.navMediator = navMediator;
        this.linksButtonsMediator = linksButtonsMediator;
        this.actionButtonsMediator = actionButtonsMediator;
        this.popupConstructor = popupConstructor;
        this.lang = lang;
    }

    _init() {
        //
        // Get data - lazy
        const contentStore = contentStoreInstance(this.lang);

        //
        // 4. Define pixelRation for TextureStorage (loader)
        const { infrastructure } = this.spaceView.threeViewShell;
        const { camera } = infrastructure;

        TexturesStorage.setPixelRatio(infrastructure.getPixelRatio());

        //
        // 5. Content units and blocks
        const {
            articleHeaderView, sectionsView, footerView, units
        } = addContent(contentStore, this.spaceView);

        //
        // Store references:
        //
        this.articleHeaderView = articleHeaderView;
        this.enablingSectionsView = [articleHeaderView, ...sectionsView, footerView];
        this.navSectionsView = [...sectionsView, footerView];
        this.units = units;
        this.unitsMap = getNameMap(units);
        this.spaceCamera = camera;
        this._currentIndex = -1;
    }

    init() {
        this._init();

        // JS handlers independent on MainApp:

        // Share buttons
        initShareButtons();

        // Disable invisible links to prevent focus action
        _initHeaderNavHelper();

        return this;
    }

    ready() {
        this.spaceView.addComponents(this.units);

        this._addViewListeners();
        this._addUnitsListeners();
        this._addPopupListeners();

        this.spaceView.start();
        this.navController.start();

        if (this.navMediator.hasStart) this.navMediator.start();

        this._updateSectionsEnableState();
        this._updateNavigationState();

        return this;
    }

    start() {
        TexturesStorage.startVideos();

        if (this.linksButtonsMediator.hasStart) this.linksButtonsMediator.start();
        if (this.actionButtonsMediator.hasStart) {
            this._addActionButtonsListeners();
            this.actionButtonsMediator.start();
        }

        return this;
    }

    async load(progressUpdate = null) {
        const localProgressUpdate = (from, to, progress) => {
            progressUpdate(from + progress * (to - from));
        };

        await FontsStorage.loadBMFs((progress) => { localProgressUpdate(0.01, 0.1, progress); });

        const contentStore = contentStoreInstance(this.lang);

        await TexturesStorage.preload(
            contentStore.texturesCollection,
            (progress) => { localProgressUpdate(0.1, 0.49, progress); }
        );
        await TexturesStorage.preload(
            contentStore.sectionsTexturesCollection,
            (progress) => { localProgressUpdate(0.5, 1, progress); }
        );

        return this;
    }

    _addViewListeners() {
        this.spaceView.addEventListener(ViewEvent.PROGRESS_CHANGED, () => {
            this._updateSectionsEnableState();
            this._updateNavigationState();
        });
    }

    _addUnitsListeners() {
        this._handleUnitAction = (evt) => {
            switch (evt.type) {
                case ActionTypes.CURRENT_MASK_LINK:
                    analyticsEvent('Outbound', 'Click', `${evt.category}. ${evt.social}. ${evt.href}`);

                    break;

                case ActionTypes.PREV_MASK:
                case ActionTypes.NEXT_MASK:
                    analyticsEvent('UI', 'Click', evt.type);

                    break;

                default:
                    analyticsEvent(evt.category, 'Click', evt.type);

                    break;
            }
        };

        this.units.forEach((comp) => {
            if (comp.clickable) {
                this._addActionsListeners(comp);
            }
        });
    }

    _addActionButtonsListeners() {
        this._addActionsListeners(this.actionButtonsMediator);

        this._focusDOMHandler = (evt) => {
            this._handleFocusDOM(evt, true);
        };
        this._blurDOMHandler = (evt) => {
            this._handleFocusDOM(evt, false);
        };
        this.actionButtonsMediator.addEventListener(DOMActionButtonEvent.FOCUS_REQUEST, this._focusDOMHandler);
        this.actionButtonsMediator.addEventListener(DOMActionButtonEvent.BLUR_REQUEST, this._blurDOMHandler);
    }

    _addActionsListeners(comp) {
        if (comp.actionTypes instanceof Array) {
            comp.actionTypes.forEach((action) => {
                switch (action) {
                    case ActionTypes.SHOW_WORK_DETAILS:
                        comp.addEventListener(action, (evt) => {
                            this._handleWorkDetailsRequest(evt);
                        });

                        break;

                    case ActionTypes.PREV_MASK_REQUEST:
                    case ActionTypes.NEXT_MASK_REQUEST:
                    case ActionTypes.CURRENT_MASK_LINK_REQUEST:
                        comp.addEventListener(action, (evt) => {
                            this._handleARRequest({ ...evt, key: evt.type });
                        });

                        break;

                    default:
                        comp.addEventListener(action, this._handleUnitAction);

                        break;
                }
            });
        } else {
            // Default action:
            comp.addEventListener(ActionTypes.SHOW_WORK_DETAILS, (evt) => {
                this._handleWorkDetailsRequest(evt);
            });
        }
    }

    _addPopupListeners() {
        // GTM
        this._handlePopupClick = (evt) => {
            const event = evt.originalEvent || evt;
            const target = event.currentTarget || event.target;

            if (!target) return;

            analyticsEvent('Outbound', capitalize(evt.type), `${target.getAttribute('aria-label')}. ${target.getAttribute('href')}`);
        };

        this.popupConstructor.addEventListener('click', this._handlePopupClick);
        this.popupConstructor.addEventListener(PopupConstructorEvent.CLOSE, (evt) => {
            this._handleCloseRequest(evt);
        });
        this.popupConstructor.addEventListener(PopupConstructorEvent.CLOSED, () => {
            this._handlePopupClosed();
        });
    }

    _handleWorkDetailsRequest(evt) {
        // SHOW POPUP

        // Forbid content change while one popup is open at the moment
        if (this.popupConstructor.isActive) return;

        if (this.popupConstructor.show(evt.workId)) {
            this.navController.pause();

            // GTM
            analyticsEvent('Artwork Sample', 'Click', `Open preview ${evt.workId}`);
        } else {
            this.navController.resume();
        }
    }

    _handleARRequest(evt) {
        const {
            unitName
        } = evt;

        const unit = this.unitsMap.get(unitName);

        console.log(
            'AR REQUEST',
            unit,
            unit.canFocusAt,
            unitName,
            evt
        );

        if (!unit) return;

        unit.callAction(evt);
    }

    _handleCloseRequest(evt) {
        // HIDE POPUP

        // GTM
        analyticsEvent('Artwork Sample', evt.action, `Close preview ${evt.workId}`);

        this.popupConstructor.hide();
        if (evt.forceNavUpdate) this.navController.resume();
    }

    _handlePopupClosed() {
        this.navController.resume();
    }

    _handleFocusDOM(evt, forth) {
        const {
            unitName,
            actionKey,
            actionElementId
        } = evt;

        const unit = this.unitsMap.get(unitName);

        /* console.log(
            unit,
            unit.canFocusAt,
            unitName,
            actionKey,
            actionElementId
        ); */

        if (!unit || !unit.canFocusAt) return;

        if (forth) unit.focusAt(actionElementId || actionKey);
        else unit.blur(actionElementId || actionKey);
    }

    _updateSectionsEnableState() {
        const camera = this.spaceCamera;

        this.enablingSectionsView.forEach((section) => {
            section.updateEnableState(camera);
        });
    }

    async _updateNavigationState() {
        if (!this.available) return;

        const indexMax = this.navSectionsView.length - 1;
        let index = this._currentIndex < 0 ? 0 : this._currentIndex;

        let section = this.navSectionsView[index];
        let check = section.checkNavigationChanged(this.spaceCamera);

        if (this._currentIndex < 0) {
            // Already Inside the Article header
            if (!check.isInside && check.direction < 0) return;
        }

        while (!check.isInside) {
            index += check.direction;

            if (index < 0) {
                check = {
                    isInside: true, direction: 0, changed: true, sectionId: ''
                };
            } else if (index > indexMax) {
                index = indexMax;
                section = this.navSectionsView[indexMax];
                check = {
                    isInside: true, direction: 0, changed: true, sectionId: section.sectionId
                };
            } else {
                section = this.navSectionsView[index];
                check = section.checkNavigationChanged(this.spaceCamera);
            }
        }

        this._currentIndex = index;
        this.navMediator.setCurrentId(check.sectionId);
    }

    get available() {
        return this.spaceView.hasRenderer;
    }
}

export default MainApp;
